import { computed, effect, inject, Injectable, Injector, runInInjectionContext, signal, untracked } from '@angular/core';
import { httpResource } from '@angular/common/http';
import { toSignal } from '@angular/core/rxjs-interop';
import { PERMISSIONS } from '../Enums/permission.enum';
import { AuthService } from '../Modules/Shared/Services/auth.service';
import { TranslationService } from '../Modules/Shared/Services/translation.service';
import { ResourceStateService } from '../Modules/Shared/Services/resource-state.service';
import { BillingService } from '../Services/billing.service';
import { LicencesService } from '../Services/licences.service';
import {
  BillingInvoice,
  BillingOverview,
  InvoicesResponse,
  OverviewResponse,
} from '../Interfaces/api/billing.interface';
import { LicenceComponentSummary, LicenceOverviewResponse } from '../Interfaces/api/licences.interface';
import { AnomaliesReportFacade } from './reports/anomalies.facade';
import { EntraAdminRolesReportFacade } from './reports/entra-admin-roles.facade';
import { M365LicenseReportFacade } from './reports/m365-license.facade';
import { HomeSignal } from '../Modules/Workspace/Interfaces/home-signal.interface';
import { HomePulseMetric } from '../Modules/Workspace/Interfaces/home-pulse-metric.interface';
import { HomeFleetStep } from '../Modules/Workspace/Interfaces/home-fleet-step.interface';
import { HomeOsShare } from '../Modules/Workspace/Interfaces/home-os-share.interface';
import { HomePosture } from '../Modules/Workspace/Types/home-posture.type';
import { HomeSignalSeverity } from '../Modules/Workspace/Types/home-signal-severity.type';
import { HomeFleetStage } from '../Modules/Workspace/Types/home-fleet-stage.type';
import { HomeFleetStepState } from '../Modules/Workspace/Types/home-fleet-step-state.type';
import {
  HOME_FLEET_STAGE_ICON,
  HOME_FLEET_STAGE_ORDER,
  HOME_FLEET_STAGE_PERMISSION,
  HOME_OS_SHARE_LIMIT,
} from '../Modules/Workspace/Consts/home-fleet-stages.const';
import {
  HOME_ACTIVE_SUBSCRIPTION_STATUSES,
  HOME_CARD_EXPIRY_WARNING_DAYS,
  HOME_GLOBAL_ADMIN_SOFT_LIMIT,
  HOME_HEALTH_SCORE_CLEAR,
  HOME_INVOICE_SAMPLE_SIZE,
  HOME_MILLISECONDS_PER_DAY,
  HOME_SEVERITY_RANK,
  HOME_UNPAID_INVOICE_STATUSES,
} from '../Modules/Workspace/Consts/home.const';
import {
  HOME_ROUTE_ANOMALIES,
  HOME_ROUTE_BILLING,
  HOME_ROUTE_ENTRA_ADMIN_ROLES,
  HOME_ROUTE_LICENCES,
  HOME_ROUTE_M365_LICENCES,
} from '../Modules/Workspace/Consts/home-routes.const';

@Injectable()
export class HomeFacade {
  private readonly _injector = inject(Injector);
  private readonly _resourceState = inject(ResourceStateService);
  private readonly _billingService = inject(BillingService);
  private readonly _licencesService = inject(LicencesService);
  private readonly _authService = inject(AuthService);
  private readonly _translationService = inject(TranslationService);

  private readonly _user = toSignal(this._authService.currentUser$, { initialValue: null });

  private readonly _canReadBilling = computed(
    () => this._user()?.hasPermission(PERMISSIONS.BILLING_MANAGE) ?? false
  );

  private readonly _canReadLicences = computed(
    () => this._user()?.hasPermission(PERMISSIONS.LICENSE_MANAGE) ?? false
  );

  private readonly _billingResource = httpResource<OverviewResponse>(() =>
    this._canReadBilling() ? this._billingService.overview() : undefined
  );

  private readonly _invoicesResource = httpResource<InvoicesResponse>(() =>
    this._canReadBilling() ? this._billingService.invoices({ page: 0, pageSize: HOME_INVOICE_SAMPLE_SIZE }) : undefined
  );

  private readonly _licencesResource = httpResource<LicenceOverviewResponse>(() =>
    this._canReadLicences() ? this._licencesService.index() : undefined
  );

  private readonly _anomalies = signal<AnomaliesReportFacade | null>(null);
  private readonly _entraAdminRoles = signal<EntraAdminRolesReportFacade | null>(null);
  private readonly _m365License = signal<M365LicenseReportFacade | null>(null);

  private readonly _scanStarted = signal(false);
  private readonly _settledStages = signal(0);
  private readonly _scanGeneration = signal(0);
  private readonly _refreshedAt = signal<Date | null>(null);
  private readonly _activatedGenerations = new Map<HomeFleetStage, number>();

  readonly fleetStages = computed<readonly HomeFleetStage[]>(() =>
    HOME_FLEET_STAGE_ORDER.filter(stage => this.isStageAllowed(stage))
  );

  readonly canScanFleet = computed(() => this.fleetStages().length > 0);
  readonly canRefreshAccount = computed(() => this._canReadBilling() || this._canReadLicences());
  readonly hasScanStarted = this._scanStarted.asReadonly();
  readonly refreshedAt = this._refreshedAt.asReadonly();

  readonly billing = computed<BillingOverview | null>(
    () => this._resourceState.valueOf(this._billingResource)?.data ?? null
  );

  readonly invoices = computed<BillingInvoice[]>(
    () => this._resourceState.valueOf(this._invoicesResource)?.data.invoices ?? []
  );

  readonly licenceComponents = computed<LicenceComponentSummary[]>(
    () => this._resourceState.valueOf(this._licencesResource)?.data.components ?? []
  );

  readonly isAccountLoading = computed(
    () =>
      this._billingResource.isLoading() ||
      this._invoicesResource.isLoading() ||
      this._licencesResource.isLoading()
  );

  readonly hasAccountError = computed(
    () =>
      this._billingResource.error() !== undefined ||
      this._invoicesResource.error() !== undefined ||
      this._licencesResource.error() !== undefined
  );

  readonly isFleetScanning = computed(
    () => this._scanStarted() && this._settledStages() < this.fleetStages().length
  );

  readonly isFleetScanComplete = computed(
    () => this._scanStarted() && this._settledStages() >= this.fleetStages().length
  );

  readonly isFleetScanPending = computed(() => this.canScanFleet() && !this._scanStarted());

  readonly fleetSteps = computed<HomeFleetStep[]>(() => this.buildFleetSteps());

  readonly healthScore = computed<number | null>(() => this._anomalies()?.healthScore() ?? null);

  readonly deviceCount = computed<number | null>(() => this.resolveDeviceCount());

  readonly osShares = computed<HomeOsShare[]>(() => this.buildOsShares());

  readonly signals = computed<HomeSignal[]>(() =>
    this.rankSignals([...this.buildAccountSignals(), ...this.buildFleetSignals()])
  );

  readonly criticalCount = computed(() => this.countBySeverity('critical'));
  readonly warningCount = computed(() => this.countBySeverity('warning'));
  readonly infoCount = computed(() => this.countBySeverity('info'));

  readonly posture = computed<HomePosture>(() => this.resolvePosture());

  readonly pulseMetrics = computed<HomePulseMetric[]>(() => this.buildPulseMetrics());

  readonly hasPulseData = computed(() => this.pulseMetrics().length > 0);

  readonly hasFleetFailure = computed(() => this.fleetSteps().some(step => step.state === 'failed'));


  constructor() {
    this.registerRefreshedAtTracking();
    this.registerFleetStageActivation();
    this.registerFleetStageCompletion();
  }

  startFleetScan(): void {
    if (!this.canScanFleet() || this._scanStarted()) return;
    this._scanStarted.set(true);
  }

  reloadAccount(): void {
    this._billingResource.reload();
    this._invoicesResource.reload();
    this._licencesResource.reload();
  }

  rescanFleet(): void {
    if (!this.isFleetScanComplete()) return;
    this._settledStages.set(0);
    this._scanGeneration.update(generation => generation + 1);
  }

  private registerRefreshedAtTracking(): void {
    effect(() => {
      if (!this.canRefreshAccount() || this.isAccountLoading() || this.hasAccountError()) return;
      this._refreshedAt.set(new Date());
    });
  }

  private registerFleetStageActivation(): void {
    effect(() => {
      if (!this._scanStarted()) return;
      const generation = this._scanGeneration();
      const stage = this.fleetStages()[this._settledStages()];
      if (stage === undefined) return;
      untracked(() => this.activateStage(stage, generation));
    });
  }

  private registerFleetStageCompletion(): void {
    effect(() => {
      if (!this._scanStarted()) return;
      const settled = this._settledStages();
      const stage = this.fleetStages()[settled];
      if (stage === undefined) return;
      if (!this.isStageSettled(stage)) return;
      this._settledStages.set(settled + 1);
    });
  }

  private activateStage(stage: HomeFleetStage, generation: number): void {
    if (this._activatedGenerations.get(stage) === generation) return;
    this._activatedGenerations.set(stage, generation);

    const isFirstScan = this.openStage(stage);
    if (!isFirstScan) this.reloadStage(stage);
  }

  private openStage(stage: HomeFleetStage): boolean {
    if (stage === 'anomalies' && this._anomalies() === null) {
      this._anomalies.set(this.materialize(AnomaliesReportFacade));
      return true;
    }
    if (stage === 'entra_admin_roles' && this._entraAdminRoles() === null) {
      this._entraAdminRoles.set(this.materialize(EntraAdminRolesReportFacade));
      return true;
    }
    if (stage === 'm365_licences' && this._m365License() === null) {
      this._m365License.set(this.materialize(M365LicenseReportFacade));
      return true;
    }
    return false;
  }

  private reloadStage(stage: HomeFleetStage): void {
    switch (stage) {
      case 'anomalies':
        this._anomalies()?.reload();
        return;
      case 'entra_admin_roles':
        this._entraAdminRoles()?.reload();
        return;
      case 'm365_licences':
        this._m365License()?.reload();
    }
  }

  private isStageLoading(stage: HomeFleetStage): boolean {
    switch (stage) {
      case 'anomalies':
        return this._anomalies()?.isLoading() ?? false;
      case 'entra_admin_roles':
        return this._entraAdminRoles()?.isLoading() ?? false;
      case 'm365_licences':
        return this._m365License()?.isLoading() ?? false;
    }
  }

  private materialize<T>(token: new (...args: never[]) => T): T {
    return runInInjectionContext(this._injector, () => this._injector.get(token));
  }

  private isStageSettled(stage: HomeFleetStage): boolean {
    if (this.isStageLoading(stage)) return false;

    switch (stage) {
      case 'anomalies':
        return this.isFacadeSettled(this._anomalies()?.data(), this._anomalies()?.error());
      case 'entra_admin_roles':
        return this.isFacadeSettled(this._entraAdminRoles()?.data(), this._entraAdminRoles()?.error());
      case 'm365_licences':
        return this.isFacadeSettled(this._m365License()?.data(), this._m365License()?.error());
    }
  }

  private isFacadeSettled(data: unknown, error: unknown): boolean {
    return (data !== null && data !== undefined) || (error !== null && error !== undefined);
  }

  private resolveStageState(stage: HomeFleetStage): HomeFleetStepState {
    const position = this.fleetStages().indexOf(stage);
    const settled = this._settledStages();
    if (!this._scanStarted() || position > settled) return 'pending';
    if (position === settled) return 'running';
    return this.hasStageFailed(stage) ? 'failed' : 'done';
  }

  private hasStageFailed(stage: HomeFleetStage): boolean {
    switch (stage) {
      case 'anomalies':
        return this._anomalies()?.data() === null;
      case 'entra_admin_roles':
        return this._entraAdminRoles()?.data() === null;
      case 'm365_licences':
        return this._m365License()?.data() === null;
    }
  }

  private isStageAllowed(stage: HomeFleetStage): boolean {
    const user = this._user();
    return (
      (user?.hasEntitlement('reports') ?? false) &&
      (user?.hasPermission(HOME_FLEET_STAGE_PERMISSION[stage]) ?? false)
    );
  }

  private resolveDeviceCount(): number | null {
    const facade = this._anomalies();
    if (facade === null || facade.data() === null) return null;
    return facade.universeCount();
  }

  private buildFleetSteps(): HomeFleetStep[] {
    return this.fleetStages().map(stage => ({
      stage,
      icon: HOME_FLEET_STAGE_ICON[stage],
      labelKey: `app.home.fleet.steps.${stage}`,
      state: this.resolveStageState(stage),
    }));
  }

  private rankSignals(signals: HomeSignal[]): HomeSignal[] {
    return [...signals].sort(
      (left, right) =>
        HOME_SEVERITY_RANK[left.severity] - HOME_SEVERITY_RANK[right.severity] ||
        (right.count ?? 0) - (left.count ?? 0) ||
        left.key.localeCompare(right.key)
    );
  }

  private countBySeverity(severity: HomeSignalSeverity): number {
    return this.signals().filter(entry => entry.severity === severity).length;
  }

  private resolvePosture(): HomePosture {
    if (this.criticalCount() > 0) return 'act';

    const score = this.healthScore();
    const isFleetBelowPar = score !== null && score < HOME_HEALTH_SCORE_CLEAR;

    return this.warningCount() > 0 || isFleetBelowPar ? 'watch' : 'clear';
  }

  private buildAccountSignals(): HomeSignal[] {
    return [...this.buildBillingSignals(), ...this.buildLicenceSignals()];
  }

  private buildBillingSignals(): HomeSignal[] {
    const overview = this.billing();
    if (overview === null) return [];

    const signals: HomeSignal[] = [];

    if (overview.is_suspended) {
      signals.push(this.buildSignal('billing_suspended', 'critical', 'gpp_bad', null, HOME_ROUTE_BILLING, {}));
    }

    const unpaid = this.invoices().filter(invoice => HOME_UNPAID_INVOICE_STATUSES.includes(invoice.status));
    if (unpaid.length > 0) {
      signals.push(
        this.buildSignal('billing_unpaid_invoice', 'critical', 'credit_card_off', unpaid.length, HOME_ROUTE_BILLING, {
          count: unpaid.length,
        })
      );
    }

    const subscription = overview.subscription;
    const isBillable = subscription !== null && HOME_ACTIVE_SUBSCRIPTION_STATUSES.includes(subscription.status);

    if (isBillable && !overview.payment_method.has_payment_method) {
      signals.push(this.buildSignal('billing_no_payment_method', 'warning', 'add_card', null, HOME_ROUTE_BILLING, {}));
    }

    if (subscription !== null && subscription.cancel_at_period_end) {
      const endsOn = this.formatDay(subscription.current_period_end);
      signals.push(
        endsOn.length > 0
          ? this.buildSignal('billing_cancelling', 'warning', 'event_busy', null, HOME_ROUTE_BILLING, { date: endsOn })
          : this.buildSignal('billing_cancelling_undated', 'warning', 'event_busy', null, HOME_ROUTE_BILLING, {})
      );
    }

    const cardExpiryDays = this.daysUntilCardExpiry(overview);
    if (cardExpiryDays !== null && cardExpiryDays < 0) {
      signals.push(this.buildSignal('billing_card_expired', 'warning', 'schedule', null, HOME_ROUTE_BILLING, {}));
    } else if (cardExpiryDays !== null && cardExpiryDays <= HOME_CARD_EXPIRY_WARNING_DAYS) {
      signals.push(
        this.buildSignal('billing_card_expiring', 'info', 'schedule', null, HOME_ROUTE_BILLING, {
          days: cardExpiryDays,
        })
      );
    }

    if (isBillable && overview.billing_contact === null) {
      signals.push(this.buildSignal('billing_no_contact', 'info', 'alternate_email', null, HOME_ROUTE_BILLING, {}));
    }

    return signals;
  }

  private buildLicenceSignals(): HomeSignal[] {
    const components = this.licenceComponents();
    if (components.length === 0) return [];

    const signals: HomeSignal[] = [];

    const free = components.reduce((total, component) => total + component.free, 0);
    if (free > 0) {
      signals.push(
        this.buildSignal('licences_unassigned', 'warning', 'inventory_2', free, HOME_ROUTE_LICENCES, { count: free })
      );
    }

    const pending = components.reduce((total, component) => total + component.pending_cancellation, 0);
    if (pending > 0) {
      signals.push(
        this.buildSignal('licences_pending_cancellation', 'info', 'unpublished', pending, HOME_ROUTE_LICENCES, {
          count: pending,
        })
      );
    }

    return signals;
  }

  private buildFleetSignals(): HomeSignal[] {
    return [...this.buildAnomalySignals(), ...this.buildEntraSignals(), ...this.buildM365Signals()];
  }

  private buildAnomalySignals(): HomeSignal[] {
    const facade = this._anomalies();
    if (facade === null || facade.universeCount() === 0) return [];

    const definitions: readonly [string, HomeSignalSeverity, string, number][] = [
      ['devices_not_encrypted', 'critical', 'no_encryption', facade.notEncrypted().length],
      ['devices_noncompliant', 'critical', 'gpp_maybe', facade.noncompliant().length],
      ['devices_no_autopilot_hash', 'warning', 'fingerprint', facade.noAutopilotHash().length],
      ['devices_inactive', 'warning', 'power_settings_new', facade.inactiveDevices().length],
      ['devices_disabled_users', 'warning', 'person_off', facade.disabledUsers().length],
      ['devices_multiple_users', 'info', 'groups', facade.multipleUsers().length],
      ['apps_failing', 'info', 'error_outline', facade.appFailure().length],
    ];

    return definitions
      .filter(([, , , count]) => count > 0)
      .map(([key, severity, icon, count]) =>
        this.buildSignal(key, severity, icon, count, HOME_ROUTE_ANOMALIES, { count })
      );
  }

  private buildEntraSignals(): HomeSignal[] {
    const facade = this._entraAdminRoles();
    if (facade === null) return [];

    const signals: HomeSignal[] = [];

    const disabled = facade.disabledWithRoles().length;
    if (disabled > 0) {
      signals.push(
        this.buildSignal('entra_disabled_with_roles', 'critical', 'lock_person', disabled, HOME_ROUTE_ENTRA_ADMIN_ROLES, {
          count: disabled,
        })
      );
    }

    const globalAdmins = facade.globalAdminCount();
    if (globalAdmins > HOME_GLOBAL_ADMIN_SOFT_LIMIT) {
      signals.push(
        this.buildSignal('entra_global_admins', 'warning', 'shield', globalAdmins, HOME_ROUTE_ENTRA_ADMIN_ROLES, {
          count: globalAdmins,
          limit: HOME_GLOBAL_ADMIN_SOFT_LIMIT,
        })
      );
    }

    return signals;
  }

  private buildM365Signals(): HomeSignal[] {
    const facade = this._m365License();
    if (facade === null) return [];

    const signals: HomeSignal[] = [];

    const expiring = facade.expiringSubscriptions().length;
    if (expiring > 0) {
      signals.push(this.buildSignal('m365_expiring', 'warning', 'hourglass_bottom', expiring, HOME_ROUTE_M365_LICENCES, {
          count: expiring,
        }));
    }

    const reclaimable = facade.wastedSeats();
    if (reclaimable > 0) {
      signals.push(this.buildSignal('m365_reclaimable', 'info', 'savings', reclaimable, HOME_ROUTE_M365_LICENCES, {
          count: reclaimable,
        }));
    }

    return signals;
  }

  private buildSignal(
    key: string,
    severity: HomeSignalSeverity,
    icon: string,
    count: number | null,
    routerLink: readonly string[],
    descriptionParams: Record<string, string | number>
  ): HomeSignal {
    return {
      key,
      severity,
      icon,
      count,
      titleKey: `app.home.signals.${key}.title`,
      descriptionKey: `app.home.signals.${key}.description`,
      descriptionParams,
      routerLink,
    };
  }

  private buildPulseMetrics(): HomePulseMetric[] {
    return [...this.buildFleetPulseMetrics(), ...this.buildLicencePulseMetrics()];
  }

  private buildFleetPulseMetrics(): HomePulseMetric[] {
    const facade = this._anomalies();
    if (facade === null || facade.universeCount() === 0) return [];

    const universe = facade.universeCount();

    return [
      this.buildMetric('compliance', 'verified_user', universe - facade.noncompliant().length, universe),
      this.buildMetric('encryption', 'lock', universe - facade.notEncrypted().length, universe),
      this.buildMetric('autopilot', 'fingerprint', universe - facade.noAutopilotHash().length, universe),
    ];
  }

  private buildLicencePulseMetrics(): HomePulseMetric[] {
    const metrics: HomePulseMetric[] = [];
    const m365 = this._m365License();

    if (m365 !== null && m365.totalSeats() > 0) {
      metrics.push(this.buildMetric('m365_seats', 'badge', m365.consumedSeats(), m365.totalSeats()));
    }

    const components = this.licenceComponents();
    const purchased = components.reduce((total, component) => total + component.purchased, 0);

    if (purchased > 0) {
      const assigned = components.reduce((total, component) => total + component.assigned, 0);
      metrics.push(this.buildMetric('avyna_seats', 'workspace_premium', assigned, purchased));
    }

    return metrics;
  }

  private buildMetric(key: string, icon: string, value: number, total: number): HomePulseMetric {
    return {
      key,
      icon,
      labelKey: `app.home.pulse.metrics.${key}`,
      value,
      total,
      ratio: total > 0 ? value / total : null,
    };
  }

  private buildOsShares(): HomeOsShare[] {
    const facade = this._anomalies();
    if (facade === null) return [];

    const distribution = facade.osEditionDistribution();
    const total = distribution.reduce((sum, datum) => sum + datum.value, 0);
    if (total === 0) return [];

    return [...distribution]
      .sort((left, right) => right.value - left.value)
      .slice(0, HOME_OS_SHARE_LIMIT)
      .map(datum => ({ name: datum.name, count: datum.value, ratio: datum.value / total }));
  }

  private daysUntilCardExpiry(overview: BillingOverview): number | null {
    const { exp_month: month, exp_year: year, has_payment_method: hasPaymentMethod } = overview.payment_method;
    if (!hasPaymentMethod || month === null || year === null) return null;

    const expiresAt = new Date(year, month, 1).getTime();
    return Math.floor((expiresAt - Date.now()) / HOME_MILLISECONDS_PER_DAY);
  }

  private formatDay(iso: string | null): string {
    if (iso === null) return '';
    const parsed = Date.parse(iso);
    if (Number.isNaN(parsed)) return '';
    return new Date(parsed).toLocaleDateString(this._translationService.currentLanguage(), {
      dateStyle: 'medium',
    });
  }
}
